import React, { useMemo, useState } from 'react';
import HarbourModal from './HarbourModal';
import HarbourNotice from './HarbourNotice';
import { apiHarbourSaveTeacherForms } from '../../utils/rmsApi';
import { harbourFormTitle } from '../../utils/harbourForms';
import { formHoldersExcept, teacherFormChanges, teacherFullName } from '../../utils/schoolTeachers';
import './harbour.css';

/*
 * "{name}'s forms" (TDI-618, built from the existing TDI-489 picker): the
 * school forms one teacher fills in for one pupil, grouped by pathway.
 * Ticks are a draft; nothing is saved until "Save and email {first name}",
 * which sends the teacher one email listing every form given in the save.
 *
 * One person holds a form at a time: a form with another teacher reads
 * "With {other name}", and ticking it asks first, then moves it on save
 * (answers saved so far stay with the form).
 *
 * groups: [{ pathway, label, formKeys }] from teacherFormGroups (only the
 * forms a teacher can have, of those the record carries). onSaved receives
 * the server's answer ({ added, removed, emailed }).
 */
export default function HarbourTeacherFormsModal({
  orgId, pupil, teacher, teachers = [], groups = [], onClose, onSaved,
}) {
  const original = useMemo(() => Object.keys(teacher.allocations || {}), [teacher]);
  const [draft, setDraft] = useState(original);
  const [moves, setMoves] = useState([]);
  const [confirming, setConfirming] = useState(null); // { formKey, holder }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const holders = useMemo(() => formHoldersExcept(teachers, teacher), [teachers, teacher]);
  const first = teacher.firstName || 'this teacher';
  const childFirst = (pupil.name || '').split(' ')[0];
  const { added, changed } = teacherFormChanges(original, draft);
  const offered = groups.flatMap((g) => g.formKeys);

  const toggle = (formKey) => {
    setError(null);
    if (draft.includes(formKey)) {
      setDraft((d) => d.filter((k) => k !== formKey));
      setMoves((m) => m.filter((k) => k !== formKey));
      return;
    }
    if (holders[formKey]) {
      setConfirming({ formKey, holder: holders[formKey] });
      return;
    }
    setDraft((d) => [...d, formKey]);
  };

  const save = async () => {
    if (!changed) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiHarbourSaveTeacherForms({
        orgId,
        patientRecordId: pupil.id,
        participantId: teacher.participantId || teacher.id,
        // In the order the picker lists them, so the email does too.
        formKeys: offered.filter((k) => draft.includes(k)),
        confirmMoves: moves.length > 0,
      });
      if (res && res.ok === false && res.code === 'HELD_BY_OTHER') {
        setError('Someone else has just been given one of these forms. Close this and open it again to see who has it now.');
        return;
      }
      onSaved(res || {});
    } catch (e) {
      setError(e?.message || 'The forms could not be saved. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (confirming) {
    const formTitle = harbourFormTitle(confirming.formKey);
    const otherName = teacherFullName(confirming.holder) || confirming.holder.email || 'another teacher';
    return (
      <HarbourModal
        title={`Give ${formTitle} to ${first} instead?`}
        confirmLabel={`Give it to ${first}`}
        onConfirm={() => {
          setDraft((d) => [...d, confirming.formKey]);
          setMoves((m) => [...m, confirming.formKey]);
          setConfirming(null);
        }}
        onClose={() => setConfirming(null)}
      >
        <p className="harbour-body-16">
          {`It's with ${otherName} now. Any answers saved so far stay with the form.`}
        </p>
      </HarbourModal>
    );
  }

  // One email per save, and only when something was given: a save that only
  // takes forms back says "Save", and nothing changed says "Done".
  const confirmLabel = added.length > 0 ? `Save and email ${first}` : changed ? 'Save' : 'Done';
  return (
    <HarbourModal
      title={`${teacherFullName(teacher) || first}'s forms`}
      confirmLabel={confirmLabel}
      busy={busy}
      onConfirm={save}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4 text-left">
        <p className="harbour-body-16">
          {`Choose the school forms ${first} fills in for ${childFirst}. We email them when you save, so they know which forms are theirs.`}
        </p>
        {groups.length === 0 && (
          <p className="harbour-body-16">
            {`There are no school forms ${first} can fill in for ${childFirst} yet.`}
          </p>
        )}
        {groups.map((group) => (
          <fieldset key={group.pathway} className="harbour-teacher-forms__group">
            <legend className="harbour-body-16-bold">{group.label}</legend>
            {group.formKeys.map((formKey) => {
              const checked = draft.includes(formKey);
              const holder = !checked ? holders[formKey] : null;
              const inputId = `teacher-form-${formKey}`;
              return (
                <div key={formKey} className="harbour-teacher-forms__row">
                  <input
                    id={inputId}
                    type="checkbox"
                    className="h-5 w-5 flex-none"
                    checked={checked}
                    disabled={busy}
                    onChange={() => toggle(formKey)}
                  />
                  <label htmlFor={inputId} className="harbour-body-16 flex flex-col">
                    <span>{harbourFormTitle(formKey)}</span>
                    {holder && (
                      <span className="harbour-record-row__meta">
                        {`With ${teacherFullName(holder) || holder.email}`}
                      </span>
                    )}
                  </label>
                </div>
              );
            })}
          </fieldset>
        ))}
        {error && (
          <HarbourNotice kind="error" title="That did not work">{error}</HarbourNotice>
        )}
      </div>
    </HarbourModal>
  );
}
